import { Directive, ElementRef, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslationService } from '../services/translation.service';

/**
 * Directive to mark elements for translation
 * Usage: <p [translate]="'unique-id'">Text to translate</p>
 * The directive will register the element for translation and update its content when language changes
 * 
 * To prevent certain words or phrases from being translated, wrap them in a span with class "notranslate":
 * Example: <div [translate]="'contact-text'">Contact your <span class="notranslate">EAP</span> for help</div>
 * 
 * This also works with dynamically generated content in TypeScript:
 * Example: const text = `Welcome to <span class="notranslate">${companyName}</span>!`;
 * 
 * Multiple notranslate spans are supported:
 * Example: Call <span class="notranslate">EAP</span> at <span class="notranslate">(555) 123-4567</span>
 */
@Directive({
	selector: '[translate]'
})
export class TranslateDirective implements OnInit, OnDestroy {
	@Input() translate: string; // The unique ID for this translatable element
	
	private originalText: string;
	private notranslateContent: string[] = [];
	private destroy$ = new Subject<void>();

	constructor(
		private el: ElementRef,
		private translationService: TranslationService,
		private renderer: Renderer2
	) {}

	ngOnInit(): void {
		// Store the original HTML content (preserves tags and interpolation)
		this.originalText = this.el.nativeElement.innerHTML?.trim() || '';

		// Listen for language changes to update text direction
		this.translationService.currentLanguage$.pipe(
			takeUntil(this.destroy$)
		).subscribe(() => {
			this.updateDirection();
		});

		// Set initial direction
		this.updateDirection();
	}

	/**
	 * Update text direction based on current language
	 */
	private updateDirection(): void {
		const isRTL = this.translationService.isRTL();
		this.renderer.setAttribute(this.el.nativeElement, 'dir', isRTL ? 'rtl' : 'ltr');
	}

	/**
	 * Apply translated text to the element
	 * This is called by the parent component after fetching translations
	 * Restores notranslate content that was replaced with placeholders
	 */
	public applyTranslation(translatedText: string): void {
		if (translatedText) {
			let finalText = translatedText;
			
			// Restore notranslate content
			if (this.notranslateContent && this.notranslateContent.length > 0) {
				this.notranslateContent.forEach((content, index) => {
					finalText = finalText.replace(
						`[[NOTRANSLATE_${index}]]`,
						`<span class="notranslate">${content}</span>`
					);
				});
			}
			
			this.renderer.setProperty(this.el.nativeElement, 'innerHTML', finalText);
		}
	}

	/**
	 * Get the original text for this element
	 * Extracts notranslate spans and replaces them with placeholders
	 */
	public getOriginalText(): string {
		let text = this.originalText;
		this.notranslateContent = [];
		
		// Find all notranslate content and replace with placeholders
		text = text.replace(/<span class="notranslate">(.*?)<\/span>/gi, (match, content) => {
			const index = this.notranslateContent.length;
			this.notranslateContent.push(content);
			return `[[NOTRANSLATE_${index}]]`;
		});
		
		return text;
	}

	/**
	 * Get the translation ID
	 */
	public getId(): string {
		return this.translate;
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
